import type { List, Map } from '@gonfalon/immutable-shim/immutableShim';
import { OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import { http, jsonToImmutable, jsonToImmutableError } from '@gonfalon/legacy-http-client/httpUtils';

import type { CodeStatisticsForFlag, Extinction, Repository } from '#utils/codeRefs/codeRefsUtils';
import { createCodeStatistics, createExtinction, createRepository } from '#utils/codeRefs/codeRefsUtils';
import { createJsonPatch } from '#utils/patchUtils';

const basePath = '/api/v2/code-refs/';
const reposPath = `${basePath}repositories`;

function getRepositoriesForFlag(projectKey: string, flagKey: string) {
  return http
    .get(`${reposPath}?projKey=${projectKey}&flagKey=${flagKey}&withBranches=1&withReferencesForDefaultBranch=1`)
    .then(jsonToImmutable)
    .then<OrderedMap<string, Repository>>((repos) =>
      repos.get('items').reduce((acc: OrderedMap<string, Repository>, curr: Repository) => {
        const currRepo = createRepository(curr);
        return acc.set(currRepo.id, currRepo);
      }, OrderedMap()),
    )
    .catch(jsonToImmutableError);
}

function getRepositories() {
  return http
    .get(reposPath)
    .then(jsonToImmutable)
    .then<OrderedMap<string, Repository>>((repos) => repos.get('items').map(createRepository))
    .catch(jsonToImmutableError);
}

function getRepositoryCount(projectKey: string, flagKey: string) {
  return http
    .get(`/api/v2/code-refs/statistics/${projectKey}${flagKey ? `?flagKey=${flagKey}` : ''}`, { beta: true })
    .then(jsonToImmutable)
    .then<OrderedMap<string, CodeStatisticsForFlag>>((stats) => stats.get('flags').map(createCodeStatistics))
    .catch(jsonToImmutableError);
}

function getExtinctions(repoName: string, branchName: string, projectKey: string, flagKey: string) {
  return http
    .get(
      `${basePath}extinctions?projKey=${projectKey}&flagKey=${flagKey}&repoName=${repoName}&branchName=${branchName}`,
    )
    .then(jsonToImmutable)
    .then<Map<string, Map<string, List<Extinction>>>>((extinctions) =>
      extinctions.get('items').map((branchExtinctions: Extinction[]) => branchExtinctions.map(createExtinction)),
    )
    .catch(jsonToImmutableError);
}

function updateRepository(oldRepo: Repository, newRepo: Repository) {
  const patch = createJsonPatch(oldRepo.toJS(), newRepo.toJS());
  return http
    .patch(oldRepo.selfLink(), {
      body: patch,
    })
    .then(jsonToImmutable)
    .then<Repository>(createRepository)
    .catch(jsonToImmutableError);
}

function deleteRepository(repo: Repository) {
  return http.delete(repo.selfLink()).catch(jsonToImmutableError);
}

export {
  getRepositories,
  getRepositoriesForFlag,
  getExtinctions,
  updateRepository,
  deleteRepository,
  getRepositoryCount,
};
